// Les pages d'accueil recomposées (D-155, maquettes https://claude.ai/artifact/WpyZwCEXC6Pj7NBY8R9g3t) : un seul ordre
// pour les neuf accueils — en-tête (une phrase), vedette, À faire, les chiffres, À venir, le club, pied. Les blocs communs :
//  - « À faire » : les cartes de l'app (ressenti, bilan, dossier, alertes FFBB, avis…) sont dessinées comme avant, dans
//    une réserve cachée (`.hm-pool`) ; chacune donne zéro, une ou plusieurs lignes, triées (une décision avant une
//    information), `max` affichées, « Tout voir · N de plus » ouvre #/a-faire avec les mêmes lignes. Un geste dans une
//    ligne passe à la carte d'origine (ses gestes restent les siens) ; la carte redessinée, la ligne suit : faite, elle
//    disparaît. Les lignes que l'accueil calcule lui-même (`data-hm-prio`) sont rangées avec elles.
//  - « Le club » : la première carte en entier, les autres repliées dans « Et aussi » ; les sollicitations (« Tu connais
//    une entreprise ? », « Tu connais quelqu'un chez… ? », la bannière partenaire) une seule à la fois, en rotation, jamais
//    en tête. Une carte sans geste (une salle confirmée, « rien à faire » de la FFBB) y va aussi.
//  - les chiffres d'une équipe façon Playbook (la saison et sa forme, le dernier score, le prochain match), la ligne de
//    l'autre casquette (« Côté joueuse : … »), le pied (le partenaire officiel en une ligne, puis l'aide).
// Une visite guidée qui vise une ligne derrière « Tout voir » ou une sollicitation qui n'est pas de tour : le guide le
// demande (`guide-reveal`, src/guide/engine.ts) et le bloc la montre.
import { teamMatches, type TeamMatch } from '../lib/api';
import { outcome } from '../domain/results';
import { addDays, localDate, parseLocalDate } from '../domain/schedule';
import { agree, playerNoun } from '../domain/labels';
import { applyHat, me, type Ctx } from './context';
import { loadAgenda } from './agenda';
import { time } from './format';
import { html, mount, openSheet, raw, type Html } from './dom';

export const COURT = html`<svg class="hero-court" viewBox="0 0 220 220" fill="none" aria-hidden="true"><circle cx="110" cy="110" r="100"/><path d="M10 110h200M110 10c-40 30-40 170 0 200M110 10c40 30 40 170 0 200"/></svg>`;

/** Une ligne d'« À faire ». */
export interface Row {
  ico: string;                       // « ti-flag » (une icône de l'app) ou un emoji
  tone?: 'warn' | 'ok';
  t: string;
  s?: string;
  href?: string;                     // la ligne mène à cette page
  /** Le bouton de la ligne : il touche celui de la carte d'origine (`fwd`), ou mène à `href`. */
  act?: { label: string; fwd?: HTMLElement | null; href?: string; ghost?: boolean };
  /** Des boutons dans la ligne (les cinq visages, les raisons, les avis) : une copie, chacun touche celui de la carte. */
  extra?: HTMLElement | null;
  /** Toute la ligne touche cet élément de la carte d'origine (une carte sans lien). */
  fwd?: HTMLElement | null;
  /** Toute la ligne ouvre la carte d'origine, entière, dans une feuille (une carte à plusieurs gestes : les salles…). */
  open?: { el: HTMLElement; title: string };
  guide?: string | null;
  prio?: number;                     // sa place, si elle n'est pas celle de sa carte
}

/** Une carte de l'accueil, dessinée dans la réserve : ses lignes d'À faire, sinon sa place dans « Le club ». */
export interface Slot {
  id: string;
  rows?: (el: HTMLElement) => (Row | null | undefined)[];
  prio?: number;                     // dans À faire : petit d'abord (une décision avant une information)
  club?: number;                     // sa place dans « Le club » quand elle ne donne pas de ligne (ou n'en donne jamais)
  rot?: boolean;                     // une sollicitation : une seule à la fois, en rotation
  label?: string;                    // son nom dans « Et aussi »
}

// --- Lire une carte de l'app -----------------------------------------------------------------------------------------
export const tx = (el: Element | null | undefined): string => (el?.textContent ?? '').replace(/\s+/g, ' ').trim();
export const pick = <T extends Element = HTMLElement>(el: ParentNode, sel: string): T | null => el.querySelector<T>(sel);
export const all = <T extends Element = HTMLElement>(el: ParentNode, sel: string): T[] => [...el.querySelectorAll<T>(sel)];
const hrefOf = (el: Element): string | undefined => el.getAttribute('href') ?? el.querySelector('a[href^="#/"]')?.getAttribute('href') ?? undefined;
const guideOf = (el: Element): string | null => el.getAttribute('data-guide') ?? el.querySelector('[data-guide]')?.getAttribute('data-guide') ?? null;

/** La ligne d'une carte de l'app (`card-row`) : son titre, son surtitre et sa phrase, son lien. */
export function cardRow(card: HTMLElement, o: Partial<Row> = {}): Row {
  const t = o.t ?? (tx(pick(card, '.card-title, .rs-card-t, strong, h3, h2')) || tx(pick(card, '.eyebrow')));
  const eb = tx(pick(card, '.eyebrow'));
  const sub = tx(pick(card, '.muted.small, .muted, small'));
  const s = o.s ?? [eb && eb !== t ? eb : '', sub && sub !== t ? sub : ''].filter(Boolean).join(' · ');
  return { ico: 'ti-check', href: hrefOf(card), guide: guideOf(card), ...o, t, s };
}

/** Les lignes de toutes les cartes d'une réserve. */
export const cardRows = (sel: string, o: Partial<Row> | ((card: HTMLElement) => Partial<Row>) = {}) =>
  (el: HTMLElement): Row[] => all(el, sel).map(c => cardRow(c, typeof o === 'function' ? o(c) : o));

/** La formation (D-141) : l'étape à faire maintenant ; le référent, les dossiers à valider. */
export const formationRows = (el: HTMLElement): Row[] => [
  ...all(el, '.fo-home').map(c => ({ ico: 'ti-grad', t: tx(pick(c, '.fo-home-t')),
    s: [tx(pick(c, '.gr-slot strong')), tx(pick(c, '.gr-slot .muted'))].filter(Boolean).join(' · '), href: c.getAttribute('href') ?? '#/formation', guide: 'home-formation' })),
  ...all(el, 'a.card-row').map(c => cardRow(c, { ico: 'ti-grad' })),
];

// --- Les gabarits ----------------------------------------------------------------------------------------------------
/** Une ligne d'À faire calculée par l'accueil (son geste est câblé par l'accueil) : `prio` la range avec les autres. */
export const fixedRow = (prio: number, r: { ico: string; tone?: 'warn' | 'ok'; t: string; s?: string; href?: string; act?: Html; guide?: string; attrs?: Html }): Html => {
  const inner = html`<span class="hm-ico ${r.tone ? `hm-${r.tone}` : ''}" aria-hidden="true">${icon(r.ico)}</span>
    <span class="todo-main"><strong>${r.t}</strong>${r.s ? html`<small>${r.s}</small>` : ''}</span>
    ${r.act ? html`<span class="hm-act">${r.act}</span>` : html`<span class="occ-go" aria-hidden="true">›</span>`}`;
  const g = r.guide ? html`data-guide="${r.guide}"` : '';
  return r.act || !r.href ? html`<div class="todo-row" data-hm-prio="${prio}" ${g} ${r.attrs ?? ''}>${inner}</div>`
    : html`<a class="todo-row" href="${r.href}" data-hm-prio="${prio}" ${g} ${r.attrs ?? ''}>${inner}</a>`;
};
const icon = (ico: string): Html | string => (ico.startsWith('ti-') ? html`<span class="ti ${ico}"></span>` : ico);

/** La carte « À faire » (vide : cachée) ; `fixed` : les lignes calculées par l'accueil. */
export const todoCard = (o: { title?: string; guide?: string; fixed?: Html[] } = {}): Html => html`
  <section class="card todo hm-todo" data-hm="todo" ${o.guide ? html`data-guide="${o.guide}"` : ''} hidden>
    <div class="hm-todo-h"><p class="eyebrow eyebrow-accent" data-hm="todo-t">${o.title ?? 'À faire'}</p></div>
    <div class="hm-rows"></div>
    <div class="hm-over" data-reveal>${o.fixed ?? ''}</div>
    <a class="hm-more" href="#/a-faire" hidden></a>
  </section>`;

/** Le bloc « Le club » (vide : caché). */
export const clubBlock = (o: { title?: string; href?: string; link?: string } = {}): Html => html`
  <section class="hm-club" data-hm="club" hidden>
    <div class="sec-head"><h3 class="sec-title">${o.title ?? 'Le club'}</h3>${o.href !== '' ? html`<a class="sec-link" href="${o.href ?? '#/club'}">${o.link ?? 'Le club →'}</a>` : ''}</div>
    <div class="hm-open"></div>
    <details class="card hm-fold" hidden><summary><strong data-hm="fold-t">Et aussi</strong><span class="occ-go" aria-hidden="true">›</span><span class="hm-chips"></span></summary>
      <div class="hm-fold-in"></div></details>
    <div class="hm-rot" data-reveal></div>
  </section>`;

/** Le pied : le partenaire officiel de l'app en une ligne (`official` : l'id de son emplacement), puis l'aide. */
export const homeFoot = (official: string | null, help: Html): Html => html`
  <div class="hm-foot">${official ? html`<div id="${official}"></div>` : ''}${help}</div>`;

/** La page « À faire » (Tout voir) : son en-tête. */
export const todoPageHead = (sub: string): Html => html`
  <header class="screen-head"><h2>À faire</h2><p class="muted">${sub}</p></header>`;

/** Un emplacement de l'accueil qui peut manquer (la page « À faire » n'a pas tout) : un bloc détaché, sans effet. */
export const spot = (root: ParentNode, sel: string): HTMLElement => root.querySelector<HTMLElement>(sel) ?? document.createElement('div');

// --- La mise en place --------------------------------------------------------------------------------------------------
/** Le jour de la rotation des sollicitations. Les essais des visites (scripts/e2e-tours.mjs, e2e-showme.mjs) le décalent
 *  (`arceau.rot` de la session) pour rejouer une visite sous chaque sollicitation du jour : un jour défavorable est
 *  couvert à chaque fois. */
const rotShift = () => { try { return Number(sessionStorage.getItem('arceau.rot')) || 0; } catch { return 0; } };
const day = () => Math.floor((Date.now() - new Date().getTimezoneOffset() * 60_000) / 86_400_000) + rotShift();
/** Le nom d'une carte dans « Et aussi » : son titre d'abord (pas le jour de sa vignette de date). */
const chipName = (el: HTMLElement) => ['.card-title', '.ce-title', 'h3', 'h2', 'strong', '.sec-title', 'b', '.eyebrow'].map(s => tx(pick(el, s))).find(Boolean) ?? '';
const filled = (el: HTMLElement) => el.childElementCount > 0 && (!!tx(el) || !!el.querySelector('img, svg'));

/** Range les cartes de l'accueil (À faire, Le club), puis à chaque carte redessinée. */
export function startHome(home: HTMLElement, o: { slots: Slot[]; max: number; title?: string; onCount?: (n: number) => void; open?: number }): void {
  const pool = home.querySelector<HTMLElement>('.hm-pool') ?? home.appendChild(Object.assign(document.createElement('div'), { className: 'hm-pool' }));
  const todo = home.querySelector<HTMLElement>('[data-hm="todo"]');
  const club = home.querySelector<HTMLElement>('[data-hm="club"]');
  const rowsBox = todo?.querySelector<HTMLElement>('.hm-rows');
  const over = todo?.querySelector<HTMLElement>('.hm-over');
  const more = todo?.querySelector<HTMLAnchorElement>('.hm-more');
  const fixed = over ? all(over, '[data-hm-prio]') : [];
  const openBox = club?.querySelector<HTMLElement>('.hm-open');
  const fold = club?.querySelector<HTMLDetailsElement>('.hm-fold');
  const foldIn = club?.querySelector<HTMLElement>('.hm-fold-in');
  const rot = club?.querySelector<HTMLElement>('.hm-rot');
  const slots = o.slots.map(s => ({ s, el: home.querySelector<HTMLElement>(`#${s.id}`) })).filter((x): x is { s: Slot; el: HTMLElement } => !!x.el);
  let showAll = !Number.isFinite(o.max);
  const revealed = new Set<string>();
  // Une sollicitation se reconnaît (les essais rejouent sous chaque rotation les visites qui en visent une).
  for (const { s, el } of slots) if (s.rot) el.dataset.hmRot = '';

  const layout = () => {
    // À faire : les lignes calculées, puis celles des cartes, triées.
    const list: { el: HTMLElement; prio: number; k: number }[] = [];
    let k = 0;
    for (const f of fixed) if (f.isConnected) list.push({ el: f, prio: Number(f.dataset.hmPrio) || 50, k: k++ });
    const toClub: { s: Slot; el: HTMLElement }[] = [];
    for (const { s, el } of slots) {
      if (el.dataset.hmLent) continue;   // ouverte dans une feuille
      const rows = s.rows && filled(el) ? s.rows(el).filter((r): r is Row => !!r && !!r.t) : [];
      if (rows.length) {
        for (const r of rows) list.push({ el: rowEl(r, pool), prio: r.prio ?? s.prio ?? 50, k: k++ });
        if (el.parentElement !== pool) pool.append(el);
      } else if (s.club != null && filled(el)) toClub.push({ s, el });
      else if (el.parentElement !== pool) pool.append(el);
    }
    list.sort((a, b) => a.prio - b.prio || a.k - b.k);
    const n = list.length, max = showAll ? Infinity : o.max;
    if (todo && rowsBox && over && more) {
      rowsBox.replaceChildren(...list.slice(0, max).map(x => x.el));
      over.replaceChildren(...list.slice(max).map(x => x.el));
      todo.hidden = !n;
      const t = todo.querySelector('[data-hm="todo-t"]');
      if (t) t.textContent = `${o.title ?? 'À faire'}${n > 1 ? ` · ${n}` : ''}`;
      more.hidden = n <= max;
      more.textContent = `Tout voir · ${n - Math.min(n, max)} de plus ›`;
    }
    o.onCount?.(n);
    // Le club : une carte en entier, le reste replié ; une seule sollicitation, en rotation.
    if (club && openBox && fold && foldIn && rot) {
      toClub.sort((a, b) => a.s.club! - b.s.club!);
      const rots = toClub.filter(x => x.s.rot);
      const chosen = rots.length ? rots[day() % rots.length] : null;
      const shown = toClub.filter(x => !x.s.rot || x === chosen || revealed.has(x.s.id));
      for (const x of toClub) if (!shown.includes(x) && x.el.parentElement !== rot) rot.append(x.el);
      const nOpen = o.open ?? 1;
      const open = shown.slice(0, nOpen), rest = shown.slice(nOpen);
      openBox.replaceChildren(...open.map(x => x.el));
      foldIn.replaceChildren(...rest.map(x => x.el));
      fold.hidden = !rest.length;
      const ft = fold.querySelector('[data-hm="fold-t"]');
      if (ft) ft.textContent = `Et aussi · ${rest.length}`;
      fold.querySelector('.hm-chips')?.replaceChildren(...rest.map(x => {
        const c = document.createElement('span');
        c.className = 'chip chip-mute';
        c.textContent = (x.s.label ?? chipName(x.el)).slice(0, 40);
        return c;
      }));
      club.hidden = !shown.length;
    }
  };

  let timer = 0;
  const obs = new MutationObserver(() => {
    if (!home.isConnected) { obs.disconnect(); return; }
    clearTimeout(timer);
    timer = window.setTimeout(run, 40);
  });
  const run = () => { if (!home.isConnected) { obs.disconnect(); return; } layout(); obs.takeRecords(); };
  run();
  obs.observe(home, { childList: true, subtree: true, characterData: true });
  // Une visite guidée qui vise une ligne derrière « Tout voir », ou une sollicitation qui n'est pas de tour.
  over?.addEventListener('guide-reveal', () => { showAll = true; run(); });
  rot?.addEventListener('guide-reveal', (e) => {
    const target = (e as CustomEvent<Element>).detail;
    const s = slots.find(x => x.el.contains(target));
    if (s) { revealed.add(s.s.id); run(); if (fold) fold.open = true; }
  });
}

/** Une ligne d'À faire tirée d'une carte : ses gestes passent à la carte d'origine. */
function rowEl(r: Row, pool: HTMLElement): HTMLElement {
  const tag = r.extra || r.act ? 'div' : r.href && !r.open ? 'a' : 'button';
  const tpl = document.createElement('template');
  tpl.innerHTML = html`<${raw(tag)} class="todo-row" ${tag === 'a' ? html`href="${r.href!}"` : tag === 'button' ? raw('type="button"') : ''} ${r.guide ? html`data-guide="${r.guide}"` : ''}>
    <span class="hm-ico ${r.tone ? `hm-${r.tone}` : ''}" aria-hidden="true">${icon(r.ico)}</span>
    <span class="todo-main"><strong>${r.t}</strong>${r.s ? html`<small>${r.s}</small>` : ''}</span>
    ${r.act ? html`<span class="hm-act">${r.act.href && !r.act.fwd ? html`<a class="btn ${r.act.ghost ? 'btn-ghost' : 'btn-primary'} btn-sm" href="${r.act.href}">${r.act.label}</a>`
      : html`<button type="button" class="btn ${r.act.ghost ? 'btn-ghost' : 'btn-primary'} btn-sm">${r.act.label}</button>`}</span>`
      : r.extra ? '' : html`<span class="occ-go" aria-hidden="true">›</span>`}
  </${raw(tag)}>`.s.trim();
  const el = tpl.content.firstElementChild as HTMLElement;
  if (r.act?.fwd) el.querySelector('.hm-act button')?.addEventListener('click', (e) => { e.preventDefault(); r.act!.fwd!.click(); });
  if (r.fwd && tag === 'button') el.addEventListener('click', () => r.fwd!.click());
  if (r.open && tag === 'button') el.addEventListener('click', () => {
    const card = r.open!.el;
    let watch: MutationObserver | null = null;
    openSheet(r.open!.title, html``, (sheet, close) => {
      card.dataset.hmLent = '1';
      sheet.querySelector('.sheet-body')?.append(card);
      // Fait : la carte se vide, la feuille se ferme (et la ligne disparaît).
      watch = new MutationObserver(() => { if (!filled(card)) close(); });
      watch.observe(card, { childList: true, subtree: true });
    }, () => { watch?.disconnect(); delete card.dataset.hmLent; pool.append(card); });
  });
  if (r.extra) {
    const copy = r.extra.cloneNode(true) as HTMLElement;
    copy.classList.add('hm-x');
    copy.removeAttribute('id');
    const src = all(r.extra, 'button, a[href]');
    all(copy, 'button, a[href]').forEach((b, i) => {
      b.removeAttribute('id');
      b.addEventListener('click', (e) => { e.preventDefault(); src[i]?.click(); });
    });
    el.querySelector('.todo-main')!.append(copy);
  }
  return el;
}

// --- Les chiffres d'une équipe --------------------------------------------------------------------------------------
/** Le début de la saison (1er août). */
const seasonStart = (today: string) => { const y = Number(today.slice(0, 4)), m = Number(today.slice(5, 7)); return `${m >= 8 ? y : y - 1}-08-01`; };
const wdShort = (d: Date) => { const s = d.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', ''); return s.charAt(0).toUpperCase() + s.slice(1); };

/** Les chiffres de l'équipe, façon Playbook : la saison (victoires, défaites, la forme des 5 derniers), le dernier score,
 *  le prochain match (`third` le remplace : le ressenti moyen chez le coach). Rien tant que la saison n'a pas de match. */
export async function renderTeamStats(el: HTMLElement, team: { id: string; name: string } | null | undefined, third: Html | null = null): Promise<void> {
  if (!team) return;
  const list = await teamMatches(team.id).catch(() => [] as TeamMatch[]);
  const today = localDate(new Date());
  const season = list.filter(m => m.events.starts_at >= seasonStart(today) && !m.events.cancelled_at).sort((a, b) => a.events.starts_at.localeCompare(b.events.starts_at));
  if (!season.length || !el.isConnected) { mount(el, html``); return; }
  const res = season.map(m => ({ m, o: outcome(m) })).filter(x => x.o !== null);
  const w = res.filter(x => x.o === 'win').length, l = res.filter(x => x.o === 'loss').length;
  const last = res.at(-1);
  const next = season.find(m => new Date(m.events.starts_at).getTime() > Date.now());
  const form = res.slice(-5).map(x => html`<i class="ms-o ${x.o === 'win' ? 'res-win' : x.o === 'loss' ? 'res-loss' : 'res-draw'}">${x.o === 'win' ? 'V' : x.o === 'loss' ? 'D' : 'N'}</i>`);
  const k1 = html`<a class="kpi" href="#/matchs"><span class="eyebrow">Saison</span><b>${w}<small>V</small> ${l}<small>D</small></b>
    ${form.length ? html`<span class="hm-form">${form}</span>` : html`<small>pas encore de résultat</small>`}</a>`;
  const k2 = last ? html`<a class="kpi" href="#/matchs"><span class="eyebrow">Dernier match</span>
      <b class="${last.o === 'win' ? 'hm-win' : last.o === 'loss' ? 'hm-loss' : ''}">${last.m.forfeit ? (last.o === 'win' ? 'V' : 'D') : html`${last.m.score_us}<small>–${last.m.score_them}</small>`}</b>
      <small>${last.o === 'win' ? 'Victoire' : last.o === 'loss' ? 'Défaite' : 'Nul'} ${last.m.is_home === false ? 'à' : 'contre'} ${last.m.opponent}</small></a>`
    : html`<a class="kpi" href="#/matchs"><span class="eyebrow">Dernier match</span><b>–</b><small>pas encore joué</small></a>`;
  let k3: Html | '' = third ?? '';
  if (!third && next) {
    const d = new Date(next.events.starts_at);
    const days = Math.round((parseLocalDate(localDate(d)).getTime() - parseLocalDate(today).getTime()) / 86_400_000);
    k3 = html`<a class="kpi" href="#/matchs"><span class="eyebrow">Prochain match</span><b>${days <= 0 ? html`J<small>-0</small>` : html`J<small>-</small>${days}`}</b>
      <small>${wdShort(d)} ${time(d)} ${next.is_home === false ? 'à' : 'contre'} ${next.opponent}</small></a>`;
  }
  mount(el, html`<section class="sec" data-hm="stats">
    <div class="sec-head"><h3 class="sec-title">${team.name}</h3><a class="sec-link" href="#/t/${team.id}">L’équipe →</a></div>
    <div class="hm-stats ${k3 ? '' : 'hm-2'}">${k1}${k2}${k3}</div></section>`);
}

// --- L'autre casquette ------------------------------------------------------------------------------------------------
/** « Côté joueuse : samedi, match · Seniors filles · tu es attendue » dans l'accueil Coach, « Côté coach : … » dans
 *  l'accueil Joueuse, dès qu'il y a une réponse attendue ou un match dans les 7 jours ; un toucher change de casquette. */
export async function renderHatLine(el: HTMLElement, ctx: Ctx, other: 'joueur' | 'coach'): Promise<void> {
  if (!ctx.hats.some(h => h.key === other) || ctx.hat === other) return;
  const c2 = { ...ctx } as Ctx;
  applyHat(c2, other);
  const ag = await loadAgenda(c2, 8).catch(() => null);
  if (!ag || !el.isConnected) return;
  const today = localDate(new Date()), end = addDays(today, 7);
  const items = ag.items.filter(it => !it.ev.cancelled_at && it.occ.date >= today && it.occ.date <= end);
  const wd = (d: string) => parseLocalDate(d).toLocaleDateString('fr-FR', { weekday: 'long' });
  let line: Html | null = null;
  if (other === 'joueur') {
    const self = me(c2);
    const mine = items.filter(it => it.mine.some(m => m.personId === self?.id));
    const g = self ? (c2.teams[0]?.gender ?? 'X') : 'X';
    const match = mine.find(it => it.ev.kind === 'match');
    const unsure = mine.find(it => it.mine.some(m => m.personId === self?.id && m.presence === 'uncertain'));
    const it = match ?? unsure;
    if (!it) return;
    const m0 = it.mine.find(m => m.personId === self?.id)!;
    const sameDay = mine.filter(x => x.ev.kind === 'match' && x.occ.date === it.occ.date).length;
    const what = it.ev.kind === 'match' ? `${wd(it.occ.date)}, ${sameDay > 1 ? 'deux matchs' : 'match'}` : `${wd(it.occ.date)}, ${it.ev.title.toLowerCase()}`;
    const st = m0.presence === 'uncertain' ? `tu es ${agree(g, 'incertain', 'incertaine')}`
      : m0.presence === 'absent' || m0.presence === 'unavailable' ? `tu es ${agree(g, 'noté absent', 'notée absente')}` : `tu es ${agree(g, 'attendu', 'attendue')}`;
    line = html`<span aria-hidden="true">🏀</span><span><b>Côté ${playerNoun(g)} : ${what}</b> · ${it.team?.name ?? c2.teams[0]?.name ?? ''} · ${st}</span>`;
  } else {
    const staff = items.filter(it => c2.coachOf.has(it.ev.team_id));
    const match = staff.find(it => it.ev.kind === 'match');
    const unsure = staff.find(it => it.uncertain.length);
    const it = match ?? unsure;
    if (!it) return;
    const g = it.team?.gender ?? 'X';
    const title = it.match ? `${it.label} ${it.match.is_home === false ? 'à' : '–'} ${it.match.opponent}` : it.ev.title;
    const extra = it.uncertain.length ? `${it.uncertain.length} ${agree(g, 'incertain', 'incertaine', it.uncertain.length)} à confirmer`
      : it.counts ? `${it.counts.present} ${agree(g, 'présent', 'présente', it.counts.present)}` : `${time(it.occ.start)}`;
    line = html`<span aria-hidden="true">📋</span><span><b>Côté coach : ${wd(it.occ.date)}, ${title}</b> · ${extra}</span>`;
  }
  mount(el, html`<a class="hm-hatline" href="#/" data-go-hat="${other}" data-guide="home-hatline">${line}<span class="occ-go" aria-hidden="true">›</span></a>`);
}
